import { useCallback, useEffect, useRef, useState } from "react";
import device from "@/services/device";

// Temporary kill switch - flip back to true to re-enable the mandatory
// location gate app-wide.
const LOCATION_GUARD_ENABLED = false;

// status: "checking" | "prompt" | "locating" | "ready" | "blocked"
const useLocationGuard = () => {
	const [status, setStatus] = useState(
		LOCATION_GUARD_ENABLED ? "checking" : "ready",
	);
	const [failed, setFailed] = useState(false);
	const permissionStatusRef = useRef(null);
	const requestingRef = useRef(false);

	// Only called from a user gesture (button click) or when permission is
	// already granted - never automatically while permission is undecided,
	// otherwise a dismissed native prompt leaves the user stuck with no way
	// to ask again. Goes through the shared device.requestLocation() so only
	// one geolocation call is ever in flight app-wide (running more than one
	// concurrently is what made the browser's permission UI flap/loop).
	const requestLocation = useCallback(() => {
		if (requestingRef.current) return;
		if (!window.navigator || !window.navigator.geolocation) {
			setStatus("blocked");
			return;
		}
		requestingRef.current = true;
		setStatus("locating");
		device
			.requestLocation()
			.then(() => {
				requestingRef.current = false;
				setFailed(false);
				setStatus("ready");
			})
			.catch((error) => {
				requestingRef.current = false;
				if (error && error.code === error.PERMISSION_DENIED) {
					setStatus("blocked");
				} else {
					// timeout / unavailable / prompt dismissed without a decision
					setFailed(true);
					setStatus("prompt");
				}
			});
	}, []);

	useEffect(() => {
		if (!LOCATION_GUARD_ENABLED) return;

		let cancelled = false;

		device.getPermissionStatus().then((permissionStatus) => {
			if (cancelled) return;

			if (permissionStatus) {
				permissionStatusRef.current = permissionStatus;
				permissionStatus.onchange = () => {
					if (permissionStatus.state === "denied") {
						setStatus("blocked");
					} else if (permissionStatus.state === "granted") {
						requestLocation();
					} else {
						setStatus("prompt");
					}
				};

				if (permissionStatus.state === "granted") {
					requestLocation();
				} else if (permissionStatus.state === "denied") {
					setStatus("blocked");
				} else {
					setStatus("prompt");
				}
				return;
			}

			// Permissions API unavailable (e.g. Safari) - ask via our own modal first
			setStatus("prompt");
		});

		return () => {
			cancelled = true;
			if (permissionStatusRef.current) {
				permissionStatusRef.current.onchange = null;
			}
		};
	}, [requestLocation]);

	// Backstop for when the Permissions API's onchange doesn't fire for a
	// given revocation path - the ongoing watch itself failing is the ground
	// truth that location is no longer available.
	useEffect(() => {
		if (!LOCATION_GUARD_ENABLED) return;

		return device.subscribePermissionLost(() => {
			setStatus("blocked");
		});
	}, []);

	return { status, failed, requestLocation };
};

export default useLocationGuard;
